"use server";

import { revalidatePath } from "next/cache";
import { redirect } from "next/navigation";
import { cookies } from "next/headers";
import { getActiveSession } from "@/lib/auth/active-session";
import { clearSession, getSession } from "@/lib/auth/cookies";
import { withTenant } from "@/lib/auth/with-tenant";
import {
  createMyChatFolder,
  deleteMyChatFolder,
  moveMyConversation,
  renameMyChatFolder,
} from "@/lib/chat/chat-folders";
import { deleteMyConversation } from "@/lib/chat/conversations";
import { chatFolderErrorMessage } from "@/lib/ui/chat-folder";
import { SIDENAV_COOKIE, sideNavCookieValue } from "@/lib/ui/nav";
import { parseTimeZone, TIMEZONE_COOKIE } from "@/lib/ui/timezone";

export type ChatFolderFormState = {
  error: string | null;
  saved: boolean;
  folderId?: string;
};

export async function pingPresenceAction() {
  const session = await getActiveSession();
  if (session) {
    return;
  }
  await clearSession();
  revalidatePath("/", "layout");
  redirect("/login");
}

export async function saveTimeZoneAction(raw: string) {
  const session = await getSession();
  if (!session) {
    return;
  }

  const zone = parseTimeZone(raw);
  if (!zone) {
    return;
  }

  const jar = await cookies();
  if (jar.get(TIMEZONE_COOKIE)?.value === zone) {
    return;
  }

  jar.set(TIMEZONE_COOKIE, zone, {
    httpOnly: true,
    sameSite: "lax",
    path: "/",
    maxAge: 60 * 60 * 24 * 365,
  });
  revalidatePath("/", "layout");
}

export async function saveSideNavExpandedAction(expanded: boolean) {
  const session = await getSession();
  if (!session) {
    return;
  }

  (await cookies()).set(SIDENAV_COOKIE, sideNavCookieValue(expanded), {
    httpOnly: true,
    sameSite: "lax",
    path: "/",
    maxAge: 60 * 60 * 24 * 365,
  });
}

export async function deleteConversationAction(id: string): Promise<boolean> {
  const session = await getSession();
  if (!session) {
    return false;
  }

  const deleted = await withTenant(() =>
    deleteMyConversation(session.userId, id),
  );
  if (deleted) {
    revalidatePath("/", "layout");
  }
  return deleted;
}

export async function createChatFolderAction(
  _prev: ChatFolderFormState,
  formData: FormData,
): Promise<ChatFolderFormState> {
  const session = await getSession();
  if (!session) {
    return { error: "Нужно войти", saved: false };
  }
  const result = await withTenant(() =>
    createMyChatFolder({
      tenantId: session.tenantId,
      userId: session.userId,
      nameRaw: String(formData.get("name") ?? ""),
    }),
  );
  if (!result.ok) {
    return { error: chatFolderErrorMessage(result.error), saved: false };
  }
  revalidatePath("/", "layout");
  return { error: null, saved: true, folderId: result.id };
}

export async function renameChatFolderAction(
  folderId: string,
  _prev: ChatFolderFormState,
  formData: FormData,
): Promise<ChatFolderFormState> {
  const session = await getSession();
  if (!session) {
    return { error: "Нужно войти", saved: false };
  }
  const result = await withTenant(() =>
    renameMyChatFolder({
      userId: session.userId,
      folderId,
      nameRaw: String(formData.get("name") ?? ""),
    }),
  );
  if (!result.ok) {
    return { error: chatFolderErrorMessage(result.error), saved: false };
  }
  revalidatePath("/", "layout");
  return { error: null, saved: true };
}

export async function deleteChatFolderAction(
  folderId: string,
): Promise<boolean> {
  const session = await getSession();
  if (!session) {
    return false;
  }
  const deleted = await withTenant(() =>
    deleteMyChatFolder({ userId: session.userId, folderId }),
  );
  if (deleted) {
    revalidatePath("/", "layout");
  }
  return deleted;
}

export async function moveConversationFolderAction(
  conversationId: string,
  folderId: string | null,
): Promise<boolean> {
  const session = await getSession();
  if (!session) {
    return false;
  }
  const result = await withTenant(() =>
    moveMyConversation({
      userId: session.userId,
      conversationId,
      folderId,
    }),
  );
  if (!result.ok) {
    return false;
  }
  revalidatePath("/", "layout");
  return true;
}
